/**
 * Helpers for the Vercel preview deployment mode.
 *
 * On Vercel preview deployments there is no per-organization subdomain to
 * derive the active org slug from, and no auth cookie/session for the target
 * environment. To still test a preview against a real organization, the tester
 * saves an organization slug and an auth token in localStorage (via the
 * SlugSelector UI). These helpers centralize the storage keys and accessors so
 * both the app UI and the shared auth hook read the exact same values.
 */

const VERCEL_SLUG_KEY = "tactic_vercel_slug";
const VERCEL_AUTH_TOKEN_KEY = "tactic_vercel_auth_token";

/**
 * Check if the app is running on a Vercel preview deployment.
 */
export const isVercelDeployment = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.location.hostname.includes("vercel.app");
};

/**
 * Get the saved organization slug from localStorage (for Vercel deployments).
 */
export const getVercelSlug = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(VERCEL_SLUG_KEY);
};

/**
 * Save the organization slug to localStorage (for Vercel deployments).
 */
export const setVercelSlug = (slug: string): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(VERCEL_SLUG_KEY, slug);
};

/**
 * Clear the saved organization slug from localStorage.
 */
export const clearVercelSlug = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(VERCEL_SLUG_KEY);
};

/**
 * Get the saved auth token from localStorage (for Vercel deployments).
 */
export const getVercelAuthToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(VERCEL_AUTH_TOKEN_KEY);
};

/**
 * Save the auth token to localStorage (for Vercel deployments).
 */
export const setVercelAuthToken = (token: string): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(VERCEL_AUTH_TOKEN_KEY, token);
};

/**
 * Clear the saved auth token from localStorage.
 */
export const clearVercelAuthToken = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(VERCEL_AUTH_TOKEN_KEY);
};
